import { SelectOption } from 'components/inputs/BaseSelect';
import SingleSelect from 'components/inputs/dropdowns/SingleSelect';
import Label from 'components/inputs/Label';
import ModalSidebar from 'components/modals/sidebar/ModalSidebar';
import ModalSidebarHeader from 'components/modals/sidebar/ModalSidebarHeader';
import Immutable from 'immutable';
import React, { useState } from 'react';
import { Submittal } from 'records/Submittal';
import * as toast from 'utils/toast';
import withAppStore, { WithAppStoreProps } from 'utils/withAppStore';
import { useTranslator } from '@bluebeam/react-i18n';
import { SubmittalReviewerResponse } from 'records/SubmittalReviewerResponse';
import SubmittalSidebarFooter from './SubmittalSidebarFooter';

interface Props extends WithAppStoreProps {
  submittal: Submittal;
  onBackClick: () => void;
  onRespond: () => void;
}

const RespondSidebar = (props: Props) => {
  const { appState } = props;
  const [updatingSubmittal, setUpdatingSubmittal] = useState<boolean>(false);
  const [nextSubmittal, setNextSubmittal] = useState<Submittal>(
    props.submittal.delete('reviewerresponse')
  );

  const { translate } = useTranslator([
    'submittalsPage',
    'buttons',
    'formLabels',
    'toastText',
  ]);

  const handleSubmittalReviewerResponseChange = (kind: string): void => {
    const { submittalReviewerResponses } = appState;

    const reviewerResponse = submittalReviewerResponses.find(
      (ss) => ss.kind === kind
    );

    if (reviewerResponse) {
      setNextSubmittal(nextSubmittal.set('reviewerresponse', reviewerResponse));
    }
  };

  const handleReviewSubmittal = async (): Promise<void> => {
    const { actions, onRespond } = props;
    try {
      setUpdatingSubmittal(true);

      await actions.reviewSubmittal(nextSubmittal, Immutable.List());

      onRespond();
    } catch (err) {
      console.error('Unable to respond to submittal', err);
      toast.error(
        translate('toastText:submittalRespondError.message'),
        translate('toastText:submittalRespondError.title')
      );
    } finally {
      setUpdatingSubmittal(false);
    }
  };

  const { onBackClick } = props;
  const { submittalReviewerResponses } = appState;

  const translatedResponses = submittalReviewerResponses.map((response) => {
    const responseType: `${string}:${string}` =
      `submittalsPage:reviewerResponse.${response.kind}` as `${string}:${string}`;

    return new SubmittalReviewerResponse({
      kind: response.kind,
      display: translate(responseType),
    });
  });

  const responses = translatedResponses.filterNot(
    (s) => s.kind === 'SUB_NOT_REVIEWED'
  );

  return (
    <ModalSidebar id="sub-viewer-modal-respond-sidebar">
      <ModalSidebarHeader
        title={translate('buttons:respond')}
        onBackClick={onBackClick}
      />
      <div className="grow-1 p4">
        <Label
          label={translate('submittalsPage:reviewerResponseTitle')}
          required
        />
        <SingleSelect
          id="submittal-viewer-respond-reviewerresponse"
          placeholder={translate('formLabels:selectWithDots')}
          value={translatedResponses.reduce<SelectOption | null>((acc, sc) => {
            if (sc.kind !== nextSubmittal.reviewerresponse.kind) {
              return acc;
            }

            return sc.toSelectOption();
          }, null)}
          options={responses.map((sc) => sc.toSelectOption()).toArray()}
          onChange={(sc: SelectOption): void => {
            handleSubmittalReviewerResponseChange(sc.value);
          }}
        />
      </div>
      <SubmittalSidebarFooter
        id="submittal-viewer-respond-sidebar-footer"
        confirmText={translate('buttons:send')}
        confirmDisabled={
          updatingSubmittal || nextSubmittal.reviewerresponse.kind.length === 0
        }
        onConfirmClick={handleReviewSubmittal}
      />
    </ModalSidebar>
  );
};

export default withAppStore<Props>(RespondSidebar);
